/**
 * /smartlinks/ab — SmartLink A/B rotation editor + assignment tester.
 *
 * Declares a TanStack Router route tied to the scaffolded `rootRoute`.
 * Rendering and validation are fully owned by the feature module — this
 * file is a thin shell with a breadcrumb + brand headline + doc hint.
 *
 * Mirrors the presentational pattern set by `campaigns.os-targeting.tsx`
 * (zinc borders, 6-8px rounded, emerald->sky gradient headline, light
 * theme only, no emojis, no competitor names).
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { SmartlinkAbPage } from "@/features/smartlinks-ab/SmartlinkAbPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/smartlinks/ab`. Supports a `?smartlinkId=` deep-link
// so operators can open the page from a SmartLink detail view with the id
// prefilled and locked.
// ---------------------------------------------------------------------------

interface SmartlinksAbSearch {
	smartlinkId?: string;
}

export const smartlinksAbRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/smartlinks/ab",
	validateSearch: (raw: Record<string, unknown>): SmartlinksAbSearch => {
		const smartlinkId = raw.smartlinkId;
		if (typeof smartlinkId === "string" && smartlinkId.trim().length > 0) {
			return { smartlinkId: smartlinkId.trim() };
		}
		return {};
	},
	component: SmartlinksAbRoute,
});

export default smartlinksAbRoute;

// ---------------------------------------------------------------------------
// Page shell — purely presentational. Delegates to the feature module.
// ---------------------------------------------------------------------------

export function SmartlinksAbRoute() {
	const { smartlinkId } = smartlinksAbRoute.useSearch();
	return (
		<main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<SmartlinkAbPage initialSmartlinkId={smartlinkId} />
			<DocHint />
		</main>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">SmartLinks</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">A/B rotation</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				A/B rotation
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Split a SmartLink's traffic across multiple offers with deterministic,
				click-stable bucketing. Weights are expressed in basis points and must
				total exactly 10,000 before a configuration can be saved.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How rotation works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Each variant carries an offer ID and a weight in basis points (1 bps
					= 0.01%).
				</li>
				<li>
					Assignment is deterministic per click ID — the same click always
					lands on the same variant while the configuration is unchanged.
				</li>
				<li>
					Changes take effect on the next click routed through the SmartLink.
				</li>
			</ul>
		</aside>
	);
}
